import { z } from "zod"
import type { ColumnSizing } from "@/lib/column-widths"

export const COLUMNS = [
  "number",
  "name",
  "applies_to",
  "price",
  "updated",
] as const

export const columnSchema = z.enum(COLUMNS)

export type Column = z.infer<typeof columnSchema>

export const FIXED_COLUMNS: readonly Column[] = ["number", "name"]

export const DEFAULT_COLUMNS: Column[] = [...COLUMNS]

export const COLUMN_LABELS: Record<Column, string> = {
  number: "Template no.",
  name: "Name",
  applies_to: "Applies to",
  price: "Price",
  updated: "Updated",
}

export const SORTABLE = ["number", "name"] as const

export const sortSchema = z.enum(SORTABLE)

export type TaskSort = z.infer<typeof sortSchema>

export function isSortable(column: Column): column is TaskSort {
  return SORTABLE.some((sortable) => sortable === column)
}

export type Slot = Column | "select" | "status" | "actions"

export const NUMBER_WIDTH = 8.5

export const STATUS_WIDTH = 2.5

export const COLUMN_SIZING: ColumnSizing<Slot> = {
  id: "template-tasks",
  widths: {
    select: 2.25,
    status: STATUS_WIDTH,
    number: NUMBER_WIDTH,
    name: 16,
    applies_to: 12,
    price: 8,
    updated: 7,
    actions: 3.25,
  },
  fill: "name",
  pinned: ["select", "status", "actions"],
  extras: ["select", "status", "actions"],
  dropOrder: ["applies_to", "updated", "price"],
}

export function withFixed(columns: Column[]): Column[] {
  return COLUMNS.filter(
    (column) => FIXED_COLUMNS.includes(column) || columns.includes(column),
  )
}

export function slotsOf(
  columns: Column[],
  selectable: boolean,
  actionable: boolean,
): Slot[] {
  const rest = COLUMNS.filter(
    (column) => column !== "number" && columns.includes(column),
  )
  return [
    ...(selectable ? (["select"] as const) : []),
    "number",
    "status",
    ...rest,
    ...(actionable ? (["actions"] as const) : []),
  ]
}
